import * as colors from "../constants/colors";
import * as breakpoints from "../breakpoints";
import * as languages from "../constants/languages";
import { css, StyleSheet } from "aphrodite";
import { english, spanish } from "../constants/strings";
import { handleUnknownError } from "../lib/errorHandler";
import { NotificationManager } from "react-notifications";
import { registerLiveClass } from "../api/class";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { rootUrl } from "../config.js";
import { format } from "date-fns";
import * as es from "date-fns/locale/es";
import * as en from "date-fns/locale/en";

const dateFormat = "ddd., MMM. D";
const timeFormat = "h:mmA";

const styles = StyleSheet.create({
	button: {
		border: `3px solid ${colors.darkTeal2}`,
		borderRadius: 16,
		backgroundColor: colors.lightTeal,
		color: colors.darkGreen,
		width: 168,
		height: 50,
		fontWeight: "bold",
		fontFamily: "priori-sans",
		fontSize: 18,
		lineHeight: 0.94,
	},
	buttonsContainer: {
		marginTop: 32,
		[breakpoints.mobileOnly]: {
			marginTop: 16,
		},
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	cancelButton: {
		backgroundColor: colors.transparent,
		color: colors.darkGreen,
		textDecoration: "underline",
		marginTop: 12,
		fontSize: 18,
		fontFamily: "priori-sans",
		border: "none",
	},
	closeIcon: {
		position: "absolute",
		top: 28,
		right: 28,
		[breakpoints.mobileOnly]: {
			right: 48,
		},
	},
	closeIconButton: {
		border: "none",
	},
	container: {
		position: "relative",
		paddingTop: 75,
		paddingLeft: 83,
		paddingRight: 77,
		paddingBottom: 96,
		backgroundColor: colors.lightTeal3,
		zIndex: 2,
		[breakpoints.mobileOnly]: {
			width: "100vw",
			paddingLeft: 39,
			paddingRight: 36,
		},

		"@media (max-height: 750px)": {
			marginBottom: 0,
			height: "100vh",
		},
	},
	errorMessage: {
		color: colors.errorRed,
		fontSize: 12,
		lineHeight: 1.67,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		marginTop: 6,
	},
	form: {
		[breakpoints.mobileOnly]: {
			paddingLeft: 20,
			paddingRight: 20,
		},
	},
	formItem: {
		marginTop: 22,
		display: "flex",
		flexDirection: "column",
	},
	fullScreenCover: {
		position: "fixed",
		height: "100vh",
		width: "100vw",
		backgroundColor: colors.transparentTeal,
		top: 0,
		left: 0,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1,

		"@media (max-height: 750px)": {
			alignItems: "flex-start",
			overflow: "auto",
		},
	},
	header: {
		fontWeight: "bold",
		fontFamily: "priori-sans",
		lineHeight: 0.94,
		textAlign: "center",
	},
	time: {
		fontWeight: "normal",
		fontFamily: "priori-sans",
		lineHeight: 0.94,
		textAlign: "center",
		borderLeft: `2px solid ${colors.gray5}`,
	},
	input: {
		width: 460,
		height: 40,
		fontSize: 15,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		border: `1px solid ${colors.gray2}`,
		[breakpoints.mobileOnly]: {
			width: "auto",
		},
	},
	inputError: {
		border: `1px solid ${colors.errorRed}`,
	},
	label: {
		lineHeight: 1.33,
		color: colors.black4,
		fontSize: 15,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		marginBottom: 10,
	},
	description: {
		textAlign: "center",
	},
	timeContainer: {
		display: "flex",
		flexDirection: "row",
		borderTop: `2px solid ${colors.gray5}`,
		borderBottom: `2px solid ${colors.gray5}`,
	},
	cell: {
		flexGrow: 1,
	},
	termsOfService: {
		fontSize: 12,
		marginTop: 8,
	},
	checkboxContainer: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
	},
	checkbox: {
		border: `1px solid ${colors.gray2}`,
		marginRight: 10,
	},
	tos: {
		maxWidth: 460,
	},
});

export default class LiveRegistrationModal extends PureComponent {
	static propTypes = {
		showing: PropTypes.bool,
		language: PropTypes.string.isRequired,
		dismiss: PropTypes.func.isRequired,
		className: PropTypes.string.isRequired,
		selectedSession: PropTypes.object,
	};

	constructor(props) {
		super(props);

		this.strings = props.language === languages.es ? spanish : english;

		this.state = {
			error: null,
			email: "",
			firstName: "",
			lastName: "",
			termsOfService: false,
		};
	}

	_dismiss = () => {
		this.setState({
			error: null,
		});
		this.props.dismiss();
	};

	_dontPropagate = e => {
		e.stopPropagation();
	};

	_setEmail = email => {
		this.setState({
			email,
		});
	};

	_setFirstName = firstName => {
		this.setState({
			firstName,
		});
	};

	_setLastName = lastName => {
		this.setState({
			lastName,
		});
	};

	_setTermsOfService = () => {
		this.setState(prevState => ({
			termsOfService: !prevState.termsOfService,
		}));
	};

	_save = e => {
		e.preventDefault();
		if (!this.props.selectedSession) {
			return;
		}

		const queryParameters = new URLSearchParams(window.location.search);

		registerLiveClass(
			this.props.selectedSession.id,
			this.state.email,
			this.state.firstName,
			this.state.lastName,
			queryParameters.get("utm_source"),
			queryParameters.get("utm_medium"),
			queryParameters.get("utm_campaign"),
			queryParameters.get("utm_content"),
		)
			.then(() => {
				this.props.dismiss();
				this.setState({
					email: "",
					firstName: "",
					lastName: "",
					termsOfService: false,
				});
				window.location.assign(
					this.props.language === languages.es
						? "https://texaswic.org/es/clases-en-linea/confirmacion-de-inscripcion"
						: "https://texaswic.org/online-classes/registration-confirmation",
				);
			})
			.catch(err => {
				handleUnknownError(err);
				NotificationManager.error(this.strings.errors.failedToRegister);
			});
	};

	render() {
		const startTime = this.props.selectedSession
			? new Date(this.props.selectedSession.startTime)
			: null;
		const endTime = this.props.selectedSession
			? new Date(this.props.selectedSession.endTime)
			: null;
		const locale = { locale: this.props.language === languages.es ? es : en };

		if (!this.props.showing) {
			return null;
		}
		return (
			<div onClick={this._dismiss} className={css(styles.fullScreenCover)}>
				<div onClick={this._dontPropagate} className={css(styles.container)}>
					<button className={css(styles.closeIconButton)} onClick={this._dismiss}>
						<img
							alt="x"
							src={`${rootUrl}/images/x.png`}
							srcSet={`${rootUrl}/images/x@2x.png 2x, ${rootUrl}/images/x@3x.png 3x`}
							width="20"
							height="20"
							className={css(styles.closeIcon)}
						/>
					</button>
					<h2 className={css(styles.header)}>{this.props.className}</h2>
					<div
						className={css(styles.description, styles.label)}
						dangerouslySetInnerHTML={{ __html: this.strings.liveClassDescription }}
					/>
					<div
						className={css(styles.description, styles.label)}
						dangerouslySetInnerHTML={{ __html: this.strings.liveClassDescription1 }}
					/>

					{startTime && endTime ? (
						<div className={css(styles.timeContainer)}>
							<div className={css(styles.header, styles.cell)}>
								<h3>{format(startTime, dateFormat, locale)}</h3>
							</div>
							<div className={css(styles.time, styles.cell)}>
								<h3>
									{`${format(startTime, timeFormat, locale)} - ${format(
										endTime,
										timeFormat,
										locale,
									)}`}
								</h3>
							</div>
						</div>
					) : null}
					<form className={css(styles.form)}>
						<div className={css(styles.formItem)}>
							<label htmlFor="email" className={css(styles.label)}>
								{this.strings.EmailAddress}
							</label>
							<input
								className={css([
									styles.input,
									this.state.error ? styles.inputError : "",
								])}
								id="email"
								type="text"
								value={this.state.email}
								onChange={e => this._setEmail(e.target.value)}
							/>
						</div>
						<div className={css(styles.formItem)}>
							<label htmlFor="first-name" className={css(styles.label)}>
								{this.strings.FirstName}
							</label>
							<input
								className={css([
									styles.input,
									this.state.error ? styles.inputError : "",
								])}
								id="first-name"
								type="text"
								value={this.state.firstName}
								onChange={e => this._setFirstName(e.target.value)}
							/>
						</div>
						<div className={css(styles.formItem)}>
							<label htmlFor="last-name" className={css(styles.label)}>
								{this.strings.LastName}
							</label>
							<input
								className={css([
									styles.input,
									this.state.error ? styles.inputError : "",
								])}
								id="last-name"
								type="text"
								value={this.state.lastName}
								onChange={e => this._setLastName(e.target.value)}
							/>
						</div>
						<div className={css(styles.formItem)}>
							<div className={css(styles.checkboxContainer)}>
								<input
									className={css([
										styles.checkbox,
										this.state.error ? styles.inputError : "",
									])}
									id="terms-of-service"
									type="checkbox"
									value={this.state.termsOfService}
									checked={this.state.termsOfService}
									onChange={this._setTermsOfService}
								/>
								<label
									htmlFor="terms-of-service"
									className={css(styles.label, styles.termsOfService)}
								>
									<div
										className={css(styles.tos)}
										dangerouslySetInnerHTML={{
											__html: this.strings.livestormTerms,
										}}
									/>
								</label>
							</div>
						</div>
						<div className={css(styles.errorMessage)}>{this.state.error}</div>
						<div className={css(styles.buttonsContainer)}>
							<button
								className={css(styles.button)}
								onClick={this._save}
								disabled={
									!this.state.email ||
									!this.state.firstName ||
									!this.state.lastName ||
									!this.state.termsOfService ||
									!this.props.selectedSession
								}
							>
								{this.strings.SUBMIT}
							</button>
							<button className={css(styles.cancelButton)} onClick={this._dismiss}>
								{this.strings.DontRegisterNow}
							</button>
						</div>
					</form>
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/LiveRegistrationModal.js